import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetItem,
  ActionsheetItemText,
} from '@/components/ui/actionsheet';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { ExpertShareTarget } from '@/util/analyticsEvents';

export interface ShareTarget {
  key: ExpertShareTarget;
  /** What the reader picks, named as the thing rather than the act. */
  label: string;
  /**
   * How the label is spoken, where that differs from how it is written.
   * Case carries pronunciation for an initialism, so it is given rather than
   * derived: lowercasing "QR" turns it from three letters into a word.
   */
  spokenLabel?: string;
}

interface ShareTargetSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Rendered in order. A target whose value is missing is left out by the caller. */
  targets: ShareTarget[];
  onSelect: (target: ShareTarget) => void;
}

/**
 * Asks which of several contact details to share, then hands the choice back.
 *
 * It renders the list it is given rather than knowing the details itself, so a
 * further one — an intake phone number, say — is an entry in the caller's array
 * and a member of `ExpertShareTarget`, not a change here. The caller is also
 * what decides whether to show this at all: with one target there is nothing to
 * choose between.
 */
export function ShareTargetSheet({ isOpen, onClose, targets, onSelect }: ShareTargetSheetProps) {
  return (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <ActionsheetBackdrop />

      <ActionsheetContent>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <VStack className="w-full px-4 py-3">
          <Text className="text-center font-semibold text-base text-typography-900">
            What would you like to share?
          </Text>
        </VStack>

        {targets.map((target) => (
          <ActionsheetItem
            key={target.key}
            onPress={() => onSelect(target)}
            accessibilityRole="button"
            accessibilityLabel={`Share ${target.spokenLabel ?? target.label}`}>
            <ActionsheetItemText className="text-base text-typography-900">
              {target.label}
            </ActionsheetItemText>
          </ActionsheetItem>
        ))}
      </ActionsheetContent>
    </Actionsheet>
  );
}
